import React from "react";
import { Check, Loader2, Sparkles, XCircle } from "lucide-react";
import { useLanguage } from "../../context/LanguageProvider";
import { getPricingPlans, GetPricingPlansResponse } from "../../api/api";
import { useQuery } from "@tanstack/react-query";

const PricingSection: React.FC = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = React.useState<'shop' | 'pharmacy' | 'restaurant' | 'hospital'>('shop');

  const { data, isLoading, isError, error } = useQuery<
    GetPricingPlansResponse,
    Error
  >({
    queryKey: ["pricing-plans"],
    queryFn: getPricingPlans,
  });

  if (isLoading) {
    return (
      <section className="py-20 bg-gray-50 relative flex justify-center items-center min-h-[400px]">
        <Loader2 className="animate-spin h-12 w-12 text-primary" />
        <span className="ml-4 text-xl text-gray-700">
          Loading Pricing Plans...
        </span>
      </section>
    );
  }

  if (isError) {
    return (
      <section className="py-20 bg-gray-50 relative text-center text-red-600">
        <XCircle className="h-12 w-12 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">Error Loading Pricing</h2>
        <p className="text-gray-600">
          {error?.message || "Failed to fetch pricing plans."}
        </p>
      </section>
    );
  }

  const plans = data?.data?.plans || [];
  let filteredPlans = plans.filter((plan: any) => plan.business_type === activeTab);

  if (activeTab === "shop" && (!filteredPlans || filteredPlans.length === 0 || !filteredPlans.some((p: any) => p.price === 0 || p.name?.toLowerCase().includes("lite")))) {
    filteredPlans = [
      {
        id: "lite",
        name: "FUKKONI Lite",
        price: 0,
        business_type: "shop",
        popular: false,
        highlightColor: "#059669",
        features: [
          t("lite_feat_1") || "Public Branded Bio Link (store.fukkoni.com/your-shop)",
          t("lite_feat_2") || "Unlimited Products, Photos & Category Collections",
          t("lite_feat_3") || "Direct WhatsApp Cart & Instant Order Notifications",
          t("lite_feat_4") || "Multi-Currency Display & MoMo / Bank Transfer Instructions",
          t("lite_feat_5") || "Customer Order Status Tracking & QR Code Sharing",
        ],
        registerUrl: "https://app.fukkoni.com/register?plan=lite",
        buttonText: t("lite_cta_btn") || "Create Free Store",
      },
      {
        id: "pro",
        name: "FUKKONI Pro POS",
        price: 20,
        business_type: "shop",
        popular: true,
        highlightColor: "#d97706",
        features: [
          t("pro_feat_all_lite") || "Everything in FUKKONI Lite",
          t("pro_feat_1") || "High-Speed Cashier POS & Thermal Receipt Printing",
          t("pro_feat_2") || "Stock Restocking, Inventory Tracking & Low Stock Alerts",
          t("pro_feat_3") || "Multi-Staff Accounts & Role-Based Permissions",
          t("pro_feat_4") || "Shift Reconciliation, Tax Compliance & Profit Margin Reports",
        ],
        registerUrl: "https://app.fukkoni.com/register?plan=pro",
        buttonText: t("pro_cta_btn") || "Start 14-Day Free Pro Trial",
      },
    ];
  }

  return (
    <section className="py-20 bg-gray-50 relative overflow-hidden" id="pricing">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-primary blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-primary blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-10">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            <span className="flex h-2 w-2 rounded-full bg-primary mr-2"></span>
            {t("pricing_badge")}
          </div>

          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
            {t("pricing_title_part1")}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
              {t("pricing_title_part2")}
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t("pricing_description")}
          </p>
        </div>

        {/* Business Type Tabs */}
        <div className="flex justify-center mb-10 sm:mb-12 px-2">
          <div className="flex flex-wrap sm:inline-flex p-1 bg-slate-200/80 backdrop-blur-md rounded-2xl gap-1 max-w-full justify-center shadow-inner">
            {[
              { id: 'shop', label: 'Boutique & Retail' },
              { id: 'pharmacy', label: 'Pharmacie' },
              { id: 'restaurant', label: 'Restaurant & Bar' },
              { id: 'hospital', label: 'Hôpital & Clinique' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-3.5 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-bold rounded-xl transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-white/40'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {filteredPlans.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 justify-center">
            {filteredPlans.map((plan, index) => (
              <div
                key={plan.id || index}
                className={`bg-white rounded-xl overflow-hidden transform transition-all duration-300 hover:-translate-y-2 hover:shadow-xl group ${
                  plan.popular
                    ? "shadow-lg ring-1 ring-primary/20"
                    : "shadow-md"
                }`}
              >
                {plan.popular && (
                  <div className="bg-gradient-to-r from-primary to-secondary text-white text-center py-2 font-medium">
                    <div className="flex items-center justify-center gap-1">
                      <Sparkles className="h-4 w-4" />
                      {t("pricing_plan_mostPopular")}
                    </div>
                  </div>
                )}

                <div className="p-8 flex flex-col h-full">
                  {" "}
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <div className="mb-6">
                    <div className="flex items-baseline">
                      <span className="text-4xl font-bold">${plan.price}</span>
                      <span className="text-gray-600 ml-1">
                        /{t("pricing_plan_month")}
                      </span>
                    </div>
                  </div>
                  <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent my-6"></div>
                  <ul className="space-y-4 mb-8 flex-grow">
                    {" "}
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center">
                        <div
                          className={`flex items-center justify-center h-5 w-5 rounded-full mr-3 bg-gradient-to-br text-white`}
                          // Assuming highlightColor is returned by the backend or derived
                          style={
                            plan.highlightColor
                              ? (plan.highlightColor.startsWith("#")
                                ? { backgroundColor: plan.highlightColor, backgroundImage: 'none' }
                                : {
                                    backgroundImage: `linear-gradient(to bottom right, var(--tw-gradient-stops))`,
                                    "--tw-gradient-from": plan.highlightColor
                                      .split(" ")[0]
                                      ?.replace("from-", "") || "",
                                    "--tw-gradient-to": (plan.highlightColor.split(" ")[1] || plan.highlightColor.split(" ")[0])
                                      ?.replace("to-", "") || "",
                                  } as any)
                              : {}
                          }
                        >
                          <Check className="h-3 w-3" />
                        </div>

                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href={plan.registerUrl || "https://app.fukkoni.com/register"}
                    className={`w-full py-3 rounded-lg font-bold transition-all duration-300 text-center ${
                      plan.popular
                        ? "bg-gradient-to-r from-primary to-secondary text-white shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/30"
                        : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"
                    }`}
                  >
                    {plan.buttonText || t("hero_button_getStarted")}
                  </a>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-md p-12 text-center max-w-lg mx-auto border border-gray-100">
            <h3 className="text-xl font-bold text-gray-800 mb-2">Custom Enterprise Plans</h3>
            <p className="text-gray-600 mb-6">
              We offer customizable enterprise packages with dedicated hosting, volume pricing, and custom integrations.
            </p>
            <a
              href="#contact"
              className="inline-block px-6 py-3 bg-primary text-white font-medium rounded-lg shadow hover:bg-primary/95 transition-all duration-200"
            >
              Contact Sales Team
            </a>
          </div>
        )}

        <div className="mt-16 text-center">
          <p className="text-gray-600">
            {t("pricing_customPlan_cta_part1")}{" "}
            <a
              href="#contact"
              className="text-primary font-medium hover:underline"
            >
              {t("pricing_customPlan_cta_part2")}
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
